import {
    EventName,
    User,
    addEventListener,
    getAccessToken,
} from '@soluto-private/mx-app-authentication';
import { getLoadState, setLoadState } from '../utils/loadState';
// eslint-disable-next-line no-restricted-imports
import { signIn, signOut } from '.';

import { Settings } from '../types';
import { getDefaultSettings } from '../settings';
import injectSDK from './injectSDK';
import { monitor } from '@soluto-private/mx-monitor';
import registerToEvents from '../events/registerToSDKEvents';
import registerToSingleSPAEvents from '../events/registerToSingleSpaEvents';
import { runActions } from '../utils/actionStore';
import { unsafeCallApi } from '../utils/apiCaller';

const registerToUserEvents = (): void => {
    addEventListener(
        EventName.LoggedIn,
        ({ user }: { user: User }) => void signIn(user.profile.asurion_id)
    );
    addEventListener(EventName.LoggedOut, () => void signOut());
    addEventListener(EventName.UserChanged, ({ user }: { user: User }) => {
        void signOut();
        void signIn(user.profile.asurion_id);
    });
};

const load = async (settings: Settings): Promise<void> => {
    await unsafeCallApi(async ({ initialize }) => await initialize(settings));
};

const isSDKLoadedExternally = () => {
    const loadState = getLoadState();

    return loadState === 'idle' && !!window.AE_SDK;
};

type InitOptions = {
    authenticated?: boolean;
    hideOnAppChange?: boolean;
    hideOnStart?: boolean;
};

const initialize = async (
    appKey: string,
    {
        authenticated = true,
        hideOnAppChange = false,
        hideOnStart,
    }: InitOptions = {}
): Promise<void> => {
    if (isSDKLoadedExternally()) {
        setLoadState('external_load');
    }

    const loadState = getLoadState();

    if (loadState !== 'idle') {
        const error = new Error('Cannot initialize SDK twice');
        monitor.warning(error.message, error);
        return;
    }

    setLoadState('loading');
    const defaultSettings = getDefaultSettings();

    const extraSettings: { isHidden?: boolean } = {};
    if (hideOnStart !== undefined) {
        extraSettings.isHidden = hideOnStart;
    }
    const settings = { ...defaultSettings, registerToEvents, ...extraSettings };

    try {
        await injectSDK(appKey);
        await load(settings);

        if (authenticated) {
            const token = getAccessToken();
            if (!token) {
                throw new Error('Access Token not provided');
            }

            await unsafeCallApi(
                async ({ auth }) => await auth.login({ token })
            );
            registerToUserEvents();
        }

        registerToSingleSPAEvents(hideOnAppChange);
        setLoadState('loaded');
        await runActions();
    } catch (error) {
        setLoadState('error');

        throw new Error(
            'Could not initialize SDK:' +
                JSON.stringify(error, Object.getOwnPropertyNames(error))
        );
    }
};

export default initialize;
